import React, { Suspense, lazy, useEffect, useState } from 'react';

/**
 * Cal.com embed — LAZY, and only rendered while the booking modal is open.
 *
 * `@calcom/embed-react/dist/Cal.es.mjs` appends
 * `<script src="https://app.cal.com/embed/embed.js">` to `document.head` from a
 * mount effect with `[]` deps and NO visibility check. A closed `<s-modal>` is
 * `display: none`, which does not stop React mounting its children or running
 * their effects — so a static import plus an always-rendered `<Cal>` pulled that
 * third-party script in on every single dashboard render.
 *
 * Export shape (verified in dist/Cal.es.mjs): `export { R as default, j as getCalApi }`
 * — `Cal` is the DEFAULT export, so `lazy(() => import(...))` needs no
 * `.then(m => ({ default: m.Cal }))` remap.
 */
const Cal = lazy(() => import('@calcom/embed-react'));

/**
 * Pro Tips - Verified against actual Nudgesmart features
 */
const PRO_TIPS = [
  "Exit-intent works on mobile too! We detect scroll-up behavior as exit intent.",
  "Spin wheels have 3x higher engagement than standard popups!",
  "Countdown timers create urgency - perfect for flash sales and limited offers.",
  "Target returning visitors differently using the 'Customer Type' condition.",
  "Use cart value targeting to show special offers when cart total exceeds a threshold.",
  "Target by time of day - show different campaigns during business hours vs evenings.",
  "Automatic holiday targeting! We detect Black Friday, Valentine's Day, and 20+ holidays.",
  "Set frequency caps to show campaigns once per session and avoid annoying visitors.",
  "Use Preview Mode to test your campaigns on desktop, tablet, and mobile before going live.",
  "Target visitors by country, region, or city - great for localized promotions!",
  "Multi-step campaigns let you collect info gradually for higher completion rates.",
];

/**
 * SupportHub Component
 *
 * Provides 4 support channels following the plan:
 * 1. Book a Call - Cal.com for free 15-min setup sessions
 * 2. Live Chat - Tawk.to SDK (floating widget, loaded separately)
 * 3. Email Support - Direct email or contact form
 * 4. Feature Requests - Submit ideas and bug reports
 *
 * Note: Tawk.to is loaded as a floating widget separately for optimal UX.
 * This component provides easy access points but the chat floats independently.
 *
 * Migrated to Polaris web components per
 * docs/architecture/polaris-web-components-dashboard-migration.md.
 * BookingModal uses App Bridge React <Modal> (renders as <ui-modal> web component).
 */
const SupportHub = ({
  shopName = '',
  shopOwner = '',
  shopEmail = '',
  shopId = '',
  shopDomain = '',
  // When provided, the "Book a Free Call" button calls THIS instead of opening
  // SupportHub's own booking modal.
  //
  // Why the override exists: the dashboard now owns a richer setup-call modal
  // (components/SetupCallPrompt) that carries segment-specific copy — a paying merchant
  // with nothing live gets a different pitch from a merchant who just installed — and the
  // same modal is what the proactive prompt opens. Routing this button there means ONE
  // booking surface, one Cal embed, one funnel in analytics.
  //
  // ⚠️ It also avoids a silent breakage: two <Cal> embeds sharing a namespace make the
  // second render an EMPTY BOX (embed-core's inline() bails on `inlineEl` already being
  // in the DOM, warning through a console.warn that vite.config.mjs strips in production).
  // With the override set, SupportHub's own booking modal is not rendered at all.
  onBookClick = null,
  subscriptionPlan = 'trial',
  trialDaysRemaining = null,
  calComLink15 = 'nudgesmart/15min',
  calComLink30 = 'nudgesmart/30min',

  emailSupport = 'denzil@nudgesmart.com',
  featureRequestUrl = 'https://github.com/sequeiradenzil/nudgesmart_feedback/discussions/categories/ideas',
  tawkPropertyId = null, // Optional: Set to enable Tawk.to
  tawkWidgetId = null,   // Optional: Set to enable Tawk.to
  compact = false,
}) => {
  // State for booking modal visibility
  const [showBookingModal, setShowBookingModal] = useState(false);
  // State for selected meeting duration (15 or 30 minutes)
  const [meetingDuration, setMeetingDuration] = useState(15);
  // State for random pro tip (rotates on each mount)
  const [currentTip] = useState(() => PRO_TIPS[Math.floor(Math.random() * PRO_TIPS.length)]);

  // Single entry point for every "book a call" affordance in this component. Routes to
  // the dashboard's richer setup-call modal when the override is supplied, otherwise
  // falls back to SupportHub's own modal (which is how it behaves standalone).
  const handleBookClick = () => {
    if (typeof onBookClick === 'function') {
      onBookClick();
      return;
    }
    setShowBookingModal(true);
  };

  // Get the active Cal.com link based on selected duration
  const activeCalLink = meetingDuration === 15 ? calComLink15 : calComLink30;

  /**
   * Cal.com booking-form prefill.
   *
   * ⚠️ KEYS ARE OMITTED WHEN BLANK, DELIBERATELY. Passing `email: ''` is NOT the same as
   * passing nothing: @calcom/embed-core serialises config with a `!== undefined` guard
   * (dist/embed/embed.js — `…of Object.entries(a))s!==void 0&&(…r.set(n,s))`), so an empty
   * string is written to the iframe URL as a real `?email=` param. Cal.com reads that as
   * "prefill present but empty", skips its own defaulting, and fills the field from the
   * LOGGED-IN Cal.com session instead — see calcom/cal.com#19471.
   *
   * That is not hypothetical: this component shipped with `email: shopEmail` where
   * shopEmail was permanently '' (providers/ShopProvider.jsx did not select `email`), and
   * the booking form showed the Nudgesmart Cal account's own address to us and a BLANK
   * field to every merchant. Measured against the live page 2026-08-23:
   *   ?name=X&email=                       → email prefilled from the Cal session  (bug)
   *   ?name=X&email=someone@example.com    → email prefilled from the param        (fixed)
   * on cal.com and on the app.cal.com/…?embed= path this component loads.
   *
   * So: send a real value, or send no key at all. Never an empty string.
   */
  // ⚠️ Trim EACH candidate before the || chain, not the result of it. `(shopOwner ||
  // shopName || '').trim()` looks equivalent and is not: a whitespace-only shopOwner is
  // truthy, so it wins the chain and THEN trims to '' — dropping the name key entirely
  // instead of falling back to shopName.
  // String() guards the throw path: `.trim()` on a truthy non-string is a TypeError, and
  // this runs in the render body, so it would take down the whole dashboard rather than
  // just the booking card. Not reachable from today's two call sites (both coerce), but
  // it costs nothing to make a future one safe.
  const prefillName = String(shopOwner || '').trim() || String(shopName || '').trim();
  const prefillEmail = String(shopEmail || '').trim();
  // The shop's myshopify domain, round-tripped through Cal.com and returned to us in
  // `payload.metadata.shopDomain` by the BOOKING_CREATED webhook
  // (api/routes/POST-cal-booking-webhook.js). That is how a booking made HERE stops the
  // proactive setup-call prompt — without it the webhook cannot tell which shop booked,
  // and a merchant who books from this card keeps being prompted.
  //
  // ⚠️ FLAT KEY, not a nested object. Measured in a browser against the real embed:
  //     { "metadata[shopDomain]": d } -> metadata%5BshopDomain%5D=d   ✅
  //     { metadata: { shopDomain: d } } -> metadata=%5Bobject+Object%5D  ❌ silent
  // The nested form is accepted, serialises to "[object Object]", and fails invisibly.
  const prefillShopDomain = String(shopDomain || '').trim();
  const calConfig = {
    theme: 'light',
    layout: 'month_view',
    hideEventTypeDetails: false,
    ...(prefillName ? { name: prefillName } : {}),
    ...(prefillEmail ? { email: prefillEmail } : {}),
    ...(prefillShopDomain ? { 'metadata[shopDomain]': prefillShopDomain } : {}),
  };

  // Note: getCalApi() removed - causes script errors in Shopify admin's nested iframe context
  // The Cal component works fine without it; getCalApi is only needed for event callbacks

  // Initialize Tawk.to chat widget
  //
  // ⚠️ `tawkPropertyId`/`tawkWidgetId` are now a DATA-EGRESS SWITCH, not just a feature
  // flag. setAttributes() below sends `shopEmail` to a third party (embed.tawk.to). That
  // was inert while shopEmail was permanently '' — providers/ShopProvider.jsx did not
  // select `email` until 2026-08-23 — so nothing real ever left the browser. It does now.
  // Neither render site in web/routes/index.jsx passes these IDs, so the path stays
  // closed; supplying them starts shipping merchant email addresses off-platform, which
  // is a privacy-policy decision, not a config tweak. (The live chat widget merchants
  // actually see is loaded separately by components/ChatWidget.jsx, which never calls
  // setAttributes.)
  useEffect(() => {
    // Only load if property and widget IDs are provided
    if (!tawkPropertyId || !tawkWidgetId) {
      return;
    }

    // Check if already loaded
    if (window.Tawk_API) {
      // Update visitor info if already loaded
      try {
        window.Tawk_API.setAttributes({
          name: shopName,
          email: shopEmail,
          plan: subscriptionPlan,
          shopId: shopId,
          trialDaysRemaining: trialDaysRemaining,
        }, (error) => {
          if (error) console.error('Tawk.to setAttributes error:', error);
        });
      } catch (e) {
        // Widget might not be fully loaded yet
      }
      return;
    }

    // Initialize Tawk.to
    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();

    // Pre-fill visitor info when widget loads
    window.Tawk_API.onLoad = function() {
      window.Tawk_API.setAttributes({
        name: shopName,
        email: shopEmail,
        plan: subscriptionPlan,
        shopId: shopId,
        trialDaysRemaining: trialDaysRemaining,
      }, (error) => {
        if (error) console.error('Tawk.to setAttributes error:', error);
      });
    };

    // Create and append script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://embed.tawk.to/${tawkPropertyId}/${tawkWidgetId}`;
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    document.head.appendChild(script);

    // Cleanup on unmount
    return () => {
      const existingScript = document.querySelector('script[src*="embed.tawk.to"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [tawkPropertyId, tawkWidgetId, shopEmail, shopName, subscriptionPlan, shopId, trialDaysRemaining]);

  // Open Tawk.to chat programmatically
  const openChat = () => {
    if (window.Tawk_API && window.Tawk_API.maximize) {
      window.Tawk_API.maximize();
    } else {
      // Fallback: open email (use window.open to avoid iframe blocking)
      window.open(`mailto:${emailSupport}`, '_blank');
    }
  };

  // Open Cal.com booking
  const openBooking = () => {
    window.open(`https://cal.com/${activeCalLink}`, '_blank');
  };

  // Open email (use window.open to avoid iframe blocking in Shopify admin)
  const openEmail = () => {
    window.open(`mailto:${emailSupport}`, '_blank');
  };

  // Open feature request
  const openFeatureRequest = () => {
    window.open(featureRequestUrl, '_blank');
  };

  // Support card item component
  const SupportItem = ({ icon, title, description, action, actionLabel, primary = false }) => (
    <s-box
      padding="base"
      background="subdued"
      borderRadius="base"
    >
      <s-stack direction="block" gap="small">
        <s-stack direction="inline" gap="small" alignItems="center">
          <s-icon type={icon} tone={primary ? 'success' : 'auto'} />
          <s-text type="strong">{title}</s-text>
        </s-stack>
        <s-text color="subdued">{description}</s-text>
        <s-button
          onClick={action}
          variant={primary ? 'primary' : 'secondary'}
        >
          {actionLabel}
        </s-button>
      </s-stack>
    </s-box>
  );

  // Sync showBookingModal state to <s-modal> imperative open/close
  useEffect(() => {
    const el = document.getElementById('booking-modal');
    if (!el) return;
    if (showBookingModal) {
      if (typeof el.showOverlay === 'function') el.showOverlay();
    } else {
      if (typeof el.hideOverlay === 'function') el.hideOverlay();
    }
  }, [showBookingModal]);

  // Sync native close (X/ESC/backdrop) back to parent state
  useEffect(() => {
    const el = document.getElementById('booking-modal');
    if (!el) return;
    const handle = () => setShowBookingModal(false);
    el.addEventListener('close', handle);
    el.addEventListener('hide', handle);
    return () => {
      el.removeEventListener('close', handle);
      el.removeEventListener('hide', handle);
    };
  }, []);

  // Booking Modal Component (shared between compact and full modes).
  // Polaris <s-modal> web component (Shopify-canonical pattern).
  // Duration toggle buttons live in the body; no footer action buttons (Cal.com
  // handles its own confirmation flow).
  //
  // NOTE: this is a plain JSX VALUE, not a nested component. Declaring
  // `const BookingModal = () => (...)` inside the render body creates a new
  // component type on every render, so React unmounts and remounts the whole
  // <s-modal> subtree each time — which detaches the `close`/`hide` listener the
  // effect above bound to the original element, and would tear down and re-create
  // the Cal.com iframe on unrelated state changes. As a value, `s-modal` is a
  // stable host element that reconciles in place, so both effects above stay
  // bound to the element that is actually in the DOM.
  const bookingModal = (
    <s-modal id="booking-modal" size="large" heading="Book a Setup Call" accessibilityLabel="Setup call booking options">
      <s-stack direction="block" gap="base">
        {/* Duration Toggle */}
        <s-stack direction="block" gap="small">
          <s-paragraph>Choose your meeting duration:</s-paragraph>
          {/* ⚠ Two ADJACENT <s-button>s, deliberately NOT wrapped in <s-button-group>.
              A toggle whose selected segment is variant="primary" CANNOT live inside a
              group — measured against the real Polaris CDN bundle:
                • group, no slot                   → renders NOTHING AT ALL (silent)
                • group + slot="secondary-actions" → renders ONLY the secondary child
                • two bare <s-button>s             → both render, correct fill
              This shipped as <s-button-group gap="none"> and the whole toggle was
              therefore invisible: merchants saw "Choose your meeting duration:" with no
              buttons under it and were silently locked to the 15-minute default.
              Fixed 2026-08-16. Full write-up: PreviewContainer.jsx:2141-2156. The
              working shape is AnalyticsDashboard.jsx:973-990. */}
          <s-stack direction="inline" gap="small-300" alignItems="center">
            <s-button
              variant={meetingDuration === 15 ? 'primary' : 'secondary'}
              onClick={() => setMeetingDuration(15)}
            >
              15 min - Quick Setup
            </s-button>
            <s-button
              variant={meetingDuration === 30 ? 'primary' : 'secondary'}
              onClick={() => setMeetingDuration(30)}
            >
              30 min - Deep Dive
            </s-button>
          </s-stack>
        </s-stack>

        {/* Calendar — third-party React component; inline style required by Cal.com embed.
            Mounted ONLY while the modal is open: mounting it inside a closed modal still
            runs its effect and injects app.cal.com/embed/embed.js into document.head. */}
        {showBookingModal && (
          <Suspense
            fallback={
              <s-box padding="large">
                <s-spinner size="large-100" accessibilityLabel="Loading calendar" />
              </s-box>
            }
          >
            {/* `key` carries BOTH prefills, and that is load-bearing: Cal.es.mjs guards its
                init with a useRef(false) once-guard, so `config` is snapshotted at MOUNT and
                never re-read — a remount is the only way a new config reaches the iframe.
                Both dynamic inputs must therefore be in the key. Keying on email alone leaves
                the name stale whenever email is unchanged while the owner name arrives, which
                is reachable because shopifyShop.email is nullable: prefillEmail stays '', the
                key never changes, and the name never lands. */}
            <Cal
              key={`${activeCalLink}|${prefillName}|${prefillEmail}|${prefillShopDomain}`}
              calLink={activeCalLink}
              style={{
                width: '100%',
                height: '500px',
                overflow: 'auto',
                borderRadius: '8px',
              }}
              config={calConfig}
            />
          </Suspense>
        )}
      </s-stack>
    </s-modal>
  );

  // Compact mode for sidebar
  if (compact) {
    return (
      <>
        {onBookClick ? null : bookingModal}
        <s-section>
          <s-stack direction="block" gap="base">
            {/* Header with System Status */}
            <s-stack direction="inline" justifyContent="space-between" alignItems="center">
              <s-heading>Need Help?</s-heading>
              <s-stack direction="inline" gap="small-100" alignItems="center">
                {/* Status indicator dot — small visual flourish, inline style required for circular shape + custom green */}
                <span
                  aria-hidden="true"
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#22c55e',
                    display: 'inline-block',
                  }}
                />
                <s-text color="subdued">All systems operational</s-text>
              </s-stack>
            </s-stack>

            {/* Book a Call - Horizontal with context */}
            {/* border="base" lifts the offer off the rest of the card. This is the one
                thing in SupportHub we actively want a merchant to act on, so it should
                not read as a peer of the email / feature-request links below it. */}
            <s-box
              padding="base"
              background="subdued"
              border="base"
              borderRadius="base"
            >
              <s-stack direction="block" gap="small">
                {/* Copy is the founder-approved offer from the marketing site's /book
                    page, not generic support language. "Book a Free Call" described the
                    MECHANISM; this describes what the merchant actually gets. The CTA is
                    "Build it with me" verbatim so it matches nudgesmart.com and the two
                    do not read as separate offers or split the analytics. */}
                <s-stack direction="inline" gap="small" alignItems="center">
                  <s-icon type="calendar" tone="success" />
                  <s-text type="strong">I'll build your first campaign with you</s-text>
                </s-stack>
                <s-text color="subdued">
                  Free · 15 minutes · nothing to buy. Screen shared, on your actual store —
                  it's live before we hang up.
                </s-text>
                <s-button variant="primary" onClick={handleBookClick}>
                  Build it with me
                </s-button>
              </s-stack>
            </s-box>

            {/* Quick Links - Horizontal row */}
            <s-stack direction="inline" gap="small" justifyContent="start">
              <s-button
                icon="email"
                onClick={openEmail}
                variant="tertiary"
              >
                Email Us
              </s-button>
              <s-button
                icon="lightbulb"
                onClick={openFeatureRequest}
                variant="tertiary"
              >
                Ideas
              </s-button>
            </s-stack>

            {/* Pro Tips Section */}
            <s-box
              padding="small"
              background="subdued"
              borderRadius="base"
            >
              <s-stack direction="block" gap="small-100">
                <s-text type="strong">Pro Tip</s-text>
                <s-text color="subdued">{currentTip}</s-text>
              </s-stack>
            </s-box>

            {/* Response time - Combined */}
            <s-text color="subdued">
              Avg response: &lt; 4 hours{tawkPropertyId && ' • Live chat available'}
            </s-text>
          </s-stack>
        </s-section>
      </>
    );
  }

  // Full mode for first-time users
  return (
    <>
      {onBookClick ? null : bookingModal}
      <s-section>
        <s-stack direction="block" gap="base">
          <s-stack direction="inline" justifyContent="space-between" alignItems="center">
            <s-heading>Need Help? We're Here For You</s-heading>
          </s-stack>

          {/* Primary: Book a Call - Opens Modal */}
          <s-box
            padding="large"
            background="subdued"
            border="base"
            borderRadius="base"
          >
            <s-stack direction="block" gap="base">
              <s-stack direction="inline" gap="small" alignItems="center">
                <s-icon type="calendar" tone="success" />
                <s-text type="strong">I'll build your first campaign with you</s-text>
              </s-stack>
              <s-text color="subdued">
                Free · 15 minutes · nothing to buy. You pick what you want to offer; I do
                the setup while you watch. It's live before we hang up.
              </s-text>
              <s-button variant="primary" onClick={handleBookClick}>
                Build it with me
              </s-button>
            </s-stack>
          </s-box>

          {/* Secondary Support Row */}
          <s-grid gridTemplateColumns="1fr 1fr" gap="base">
            <SupportItem
              icon="chat"
              title="Live Chat"
              description="Chat with support now"
              action={openChat}
              actionLabel="Chat Now"
            />
            <SupportItem
              icon="email"
              title="Email Support"
              description={emailSupport}
              action={openEmail}
              actionLabel="Send Email"
            />
          </s-grid>

          {/* Feature Request */}
          <s-box
            padding="base"
            background="subdued"
            borderRadius="base"
          >
            <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="small">
              <s-stack direction="block" gap="small-100">
                <s-stack direction="inline" gap="small" alignItems="center">
                  <s-icon type="lightbulb" />
                  <s-text type="strong">Feature Requests</s-text>
                </s-stack>
                <s-text color="subdued">Submit ideas &amp; feedback</s-text>
              </s-stack>
              <s-button onClick={openFeatureRequest}>
                Submit
              </s-button>
            </s-stack>
          </s-box>

          {/* Response time indicator */}
          <s-text color="subdued">
            Average response time: &lt; 4 hours
          </s-text>
        </s-stack>
      </s-section>
    </>
  );
};

export default SupportHub;
